import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { OutletContextType } from "../../types/GlobalType";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { capitalizeFirstLetter } from "../../_services/utils.jsx";

import {
  classeController,
  domainController,
  functionController,
  gradeController,
  montantController,
  schoolYearController,
  terminalController,
} from "../../_api/controllers/scolarite.controller.ts";
import moment from "moment";
import * as Yup from "yup";
import HasRole from "../../component/_elements/HasRole.tsx";

//  the skeleton component
const ScolariteAccordionSkeleton = ({
  isDarkMode,
}: {
  isDarkMode: boolean;
}) => {
  return (
    <div className="space-y-5">
      {/* Tab bar skeleton */}
      <div
        className={`flex gap-2 rounded-xl border p-1 ${
          isDarkMode
            ? "border-[#30363D] bg-[#161B22]/70"
            : "border-gray-200 bg-gray-50/80"
        }`}
      >
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="h-9 w-40 rounded-lg bg-gray-200 dark:bg-[#21262D] animate-pulse"
          ></div>
        ))}
      </div>
      <div
        className={`rounded-xl overflow-hidden border animate-pulse ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <div
          className={`p-5 space-y-3 ${
            isDarkMode ? "bg-[#0D1117]" : "bg-white"
          }`}
        >
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="h-10 w-full rounded bg-gray-300 dark:bg-gray-700"
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

const schoolYearSchema = Yup.object()
  .shape({
    schoolYear: Yup.string()
      .required("L’année scolaire est obligatoire.")
      .matches(/^\d{4}-\d{4}$/, "Format invalide. Exemple: 2025-2026")
      .test(
        "valid-range",
        "Les années doivent se suivre (ex: 2025-2026)",
        (value) => {
          if (!value) return false;
          const [start, end] = value.split("-").map(Number);
          return end === start + 1;
        },
      ),

    startDate: Yup.date()
      .typeError("La date de début est invalide")
      .required("La date de début est obligatoire"),

    endDate: Yup.date()
      .typeError("La date de fin est invalide")
      .required("La date de fin est obligatoire")
      .min(
        Yup.ref("startDate"),
        "La date de fin doit être postérieure à la date de début",
      ),
  })
  // Custom validation for date inclusion inside schoolYear
  .test(
    "dates-in-school-year",
    "Les dates doivent être comprises dans l’année scolaire sélectionnée",
    (values) => {
      const { schoolYear, startDate, endDate } = values || {};
      if (!schoolYear || !startDate || !endDate) return true; // skip if incomplete

      const [startYear, endYear] = schoolYear.split("-").map(Number);
      const schoolStart = new Date(`${startYear}-01-01`);
      const schoolEnd = new Date(`${endYear}-12-31`);

      const start = new Date(startDate);
      const end = new Date(endDate);

      return start >= schoolStart && end <= schoolEnd;
    },
  );

const Scolarite: React.FC = () => {
  const { isDarkMode, setIsDarkMode } = useOutletContext<OutletContextType>();
  const [showAddYearModal, setShowAddYearModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<null | string>(null);
  const [notification, setNotification] = useState<any>(null); // { message, type }
  const [searchQuery, setSearchQuery] = useState("");
  const [searchQueryFrais, setSearchQueryFrais] = useState("");

  //filter for montant (Frais)
  const [filterMontantByClasse, setFilterMontantByClasse] =
    useState("Toutes les classes");
  const [isMontantClassDropdownOpen, setIsMontantClassDropdownOpen] =
    useState(false);
  const [filterMontantSy, setFilterMontantBySy] = useState("");
  const [isMontantSyDropdownOpen, setIsMontantSyDropdownOpen] = useState(false);

  //filter for classe
  const [filtrerClasseByYear, setfiltrerClasseByYear] =
    useState("Toutes les années");
  const [isYearClassDropdownOpen, setIsYearClassDropdownOpen] = useState(false);

  const showNotification = (message: string, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  //handleling delete & activate & desactivate items
  interface ActionState {
    actionSection: string;
    actionType: string;
    actionText: string;
    currentId: any;
  }

  interface Action {
    type: "DELETE" | "DESACTIVATE" | "CANCEL" | "ACTIVATE";
    payload: { actionText: string; actionSection: string; currentId: any };
  }
  const ACTION_TYPES = {
    DELETE: "DELETE",
    DESACTIVATE: "DESACTIVATE",
    ACTIVATE: "ACTIVATE",
    CANCEL: "CANCEL",
  };

  const INITIAL_STATE: ActionState = {
    actionSection: "",
    actionType: "",
    actionText: "",
    currentId: null,
  };

  const actionReducer = (state: ActionState, action: Action): ActionState => {
    switch (action.type) {
      case ACTION_TYPES.DELETE:
        return {
          ...state,
          actionType: "delete",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };

      case ACTION_TYPES.ACTIVATE:
        return {
          ...state,
          actionType: "activate",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };
      case ACTION_TYPES.DESACTIVATE:
        return {
          ...state,
          actionType: "desactivate",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };
      case ACTION_TYPES.CANCEL:
        return {
          actionSection: "",
          actionType: "",
          actionText: "",
          currentId: null,
        };

      default:
        return state;
    }
  };

  const [state_actions, dispatch] = React.useReducer(
    actionReducer,
    INITIAL_STATE,
  );

  const [newSchoolYear, setNewSchoolYear] = useState({
    title: "",
    startDate: "",
    endDate: "",
    isActive: false,
  });

  const [editSchoolYearData, setEditSchoolYearData] = useState<{
    id?: any;
    title: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
    type: string;
  }>({
    id: "",
    title: "",
    startDate: "",
    endDate: "",
    isActive: false,
    type: "",
  });

  //class
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [showEditClassModal, setShowEditClassModal] = useState(false);
  const [newClass, setNewClass] = useState({
    name: "",
    code: "",
    level: "",
    schoolYear: "",
  });
  const [editClassData, setEditClassData] = useState<{
    id: any;
    name: string;
    code: string;
    level: string;
    schoolYear: string;
  }>({
    id: "",
    name: "",
    code: "",
    level: "",
    schoolYear: "",
  });

  //Domaine
  const [showAddDomaineModal, setShowAddDomaineModal] = useState(false);
  const [showEditDomaineModal, setShowEditDomaineModal] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [newDomain, setNewDomain] = useState({
    designation: "",
    description: "",
  });
  const [editDomainData, setEditDomainData] = useState<{
    id: any;
    designation: string;
    description: string;
  }>({
    id: null,
    designation: "",
    description: "",
  });

  //function
  const [showAddFunctionModal, setShowAddFunctionModal] = useState(false);
  const [showEditFunctionModal, setShowEditFunctionModal] = useState(false);
  const [charCountFunction, setCharCountFunction] = useState(0);
  const [newFunction, setNewFunction] = useState({
    designation: "",
    description: "",
  });
  const [editFunctionData, setEditFunctionData] = useState<{
    id: any;
    designation: string;
    description: string;
  }>({
    id: null,
    designation: "",
    description: "",
  });

  //grade
  const [showAddGradeModal, setShowAddGradeModal] = useState(false);
  const [showEditGradeModal, setShowEditGradeModal] = useState(false);
  const [charCountGrade, setCharCountGrade] = useState(0);
  const [newGrade, setNewGrade] = useState({
    designation: "",
    description: "",
  });
  const [editGradeData, setEditGradeData] = useState<{
    id: any;
    designation: string;
    description: string;
  }>({
    id: null,
    designation: "",
    description: "",
  });

  //montant
  const [showAddMontantModal, setShowAddMontantModal] = useState(false);
  const [showEditMontantModal, setShowEditMontantModal] = useState(false);
  const [newMontant, setNewMontant] = useState({
    montant: "",
    classe_id: "",
    annee_scolaire_id: "",
  });
  const [editMontantData, setEditMontantData] = useState<{
    id: any;
    montant: any;
    classDesignation?: any;
    classe_id: any;
    annee_scolaire_id: any;
  }>({
    id: "",
    montant: "",
    classe_id: "",
    annee_scolaire_id: "",
  });

  //Terminaux
  const [showAddTerminalModal, setShowAddTerminalModal] = useState(false);
  const [showEditTerminalModal, setShowEditTerminalModal] = useState(false);
  const [newTerminal, setNewTerminal] = useState<{
    uid: string;
    designation: string;
  }>({
    uid: "",
    designation: "",
  });
  const [editTerminalData, setEditTerminalData] = useState<{
    id: any;
    uid: string;
    designation: string;
  }>({
    id: "",
    uid: "",
    designation: "",
  });

  // Navigation par onglets (remplace l'ancien système d'accordéons empilés).
  // "anneeScolaire" est ouvert par défaut, comme l'était le premier accordéon.
  const [activeTab, setActiveTab] = useState<string>("anneeScolaire");

  const toggleYearClassDropdown = () => {
    setIsYearClassDropdownOpen(!isYearClassDropdownOpen);
  };
  const toggleMontantClassDropdown = () => {
    setIsMontantClassDropdownOpen(!isMontantClassDropdownOpen);
  };
  const toggleMontantSyDropdown = () => {
    setIsMontantSyDropdownOpen(!isMontantSyDropdownOpen);
  };

  // Fermeture des menus lors d'un clic à l'extérieur
  const montantClassDropdownRef = React.useRef<HTMLDivElement>(null);
  const montantSyDropdownRef = React.useRef<HTMLDivElement>(null);
  const yearClassDropdownRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //status button
      if (
        yearClassDropdownRef.current &&
        !yearClassDropdownRef.current.contains(event.target as Node)
      ) {
        setIsYearClassDropdownOpen(false);
      }

      if (
        montantClassDropdownRef.current &&
        !montantClassDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMontantClassDropdownOpen(false);
      }

      if (
        montantSyDropdownRef.current &&
        !montantSyDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMontantSyDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const queryClient = useQueryClient();

  //fetch anne scolaire
  const { data: schoolYearData, isLoading: isLoadingSchoolYear } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });
  //fetch all classes
  const { data: classeData, isLoading: isLoadingClasses } = useQuery({
    queryKey: ["classes"],
    queryFn: classeController.getAll,
  });
  //fetch all domains
  const { data: domainData } = useQuery({
    queryKey: ["domains"],
    queryFn: domainController.getAll,
  });

  //fetch all functions
  const { data: functionData } = useQuery({
    queryKey: ["functions"],
    queryFn: functionController.getAll,
  });
  //fetch all grade
  const { data: gradeData } = useQuery({
    queryKey: ["grades"],
    queryFn: gradeController.getAll,
  });
  //fetch all montant
  const { data: montantData, isLoading: isLoadingMontant } = useQuery({
    queryKey: ["montants"],
    queryFn: montantController.getAll,
  });
  //fetch all ontant
  const { data: terminalData } = useQuery({
    queryKey: ["terminaux"],
    queryFn: terminalController.getAll,
  });

  const schoolYearArr = schoolYearData?.data || [];
  const classeDataArr = classeData?.data || [];
  const domainDataArr = domainData?.data || [];
  const functionDataArr = functionData?.data || [];
  const gradeDataArr = gradeData?.data || [];
  const montantDataArr = montantData?.data || [];
  const terminalDataArr = terminalData?.data || [];

  //get all years for classes date created at
  const years = [
    ...new Set(
      classeDataArr.map((item) => moment(item.created_at).format("YYYY")),
    ),
  ];

  //get active school year
  const activeYear = schoolYearArr.filter((item) => item.status)[0]
    ?.designation;

  const activeYearId = schoolYearArr.filter((item) => item.status)[0]?.id;

  // Le filtre des frais démarre toujours sur l’année scolaire active.
  React.useEffect(() => {
    if (activeYear && !filterMontantSy) {
      setFilterMontantBySy(activeYear);
    }
  }, [activeYear, filterMontantSy]);

  const selectedMontantYear = schoolYearArr.find(
    (year) => year.designation === filterMontantSy,
  );
  const selectedMontantYearId = selectedMontantYear?.id;
  const isActiveMontantYear =
    Number(selectedMontantYearId) === Number(activeYearId);

  //get Complete montant per class array
  const completeMontantArray = montantDataArr.map((montant) => {
    let classeDesignation: any, anneeScolaireDesignation: any;
    for (let classe of classeDataArr) {
      if (classe.id === montant.classe_id) {
        classeDesignation = classe.designation;
      }
    }
    for (let sy of schoolYearArr) {
      if (sy.id === montant.annee_scolaire_id) {
        anneeScolaireDesignation = sy.designation;
      }
    }
    return {
      ...montant,
      classeDesignation,
      anneeScolaireDesignation,
    };
  });

  //filter class
  const filteredClasses = classeDataArr.filter((classe) => {
    const matchesSearch =
      classe.designation.toLowerCase().includes(searchQuery.toLowerCase()) ||
      classe.code.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesYear =
      filtrerClasseByYear === "Toutes les années" ||
      classe.created_at.includes(filtrerClasseByYear);

    return matchesSearch && matchesYear;
  });

  //filter montant
  // Le tableau part des classes (et non des montants) afin qu’une classe soit
  // toujours affichée une seule fois, même lorsqu’aucun frais n’est défini.
  const uniqueClassesForMontant = Array.from(
    new Map(classeDataArr.map((classe) => [classe.id, classe])).values(),
  );

  const filteredMontants = uniqueClassesForMontant
    .map((classe) => {
      const montantForSelectedYear = completeMontantArray.find(
        (montant) =>
          Number(montant.classe_id) === Number(classe.id) &&
          Number(montant.annee_scolaire_id) === Number(selectedMontantYearId),
      );

      return {
        ...montantForSelectedYear,
        id: montantForSelectedYear?.id ?? null,
        classe_id: classe.id,
        classeDesignation: classe.designation,
        montant: montantForSelectedYear?.montant ?? null,
        annee_scolaire_id: selectedMontantYearId,
        anneeScolaireDesignation: filterMontantSy,
        hasMontant: Boolean(montantForSelectedYear),
      };
    })
    .filter((item) => {
      const matchesSearch = item.classeDesignation
        ?.toLowerCase()
        .includes(searchQueryFrais?.toLowerCase());

      const matchesClass =
        filterMontantByClasse === "Toutes les classes" ||
        item.classeDesignation?.toLowerCase() ===
          filterMontantByClasse?.toLowerCase();

      return matchesSearch && matchesClass;
    });

  const mutationCreateShoolYear = useMutation({
    mutationFn: () =>
      schoolYearController.create({
        designation: newSchoolYear.title,
        date_debut: newSchoolYear.startDate,
        date_fin: newSchoolYear.endDate,
      }),

    onSuccess: (data) => {
      setShowAddYearModal(false);
      showNotification("Enregistré avec succès", "success");
      setNewSchoolYear({
        title: "",
        startDate: "",
        endDate: "",
        isActive: false,
      });
      queryClient.invalidateQueries({ queryKey: ["schoolYears"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateShoolYear = useMutation({
    mutationFn: () =>
      schoolYearController.update(editSchoolYearData.id, {
        designation: editSchoolYearData.title,
        date_debut: editSchoolYearData.startDate,
        date_fin: editSchoolYearData.endDate,
      }),

    onSuccess: (data) => {
      setShowEditModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditSchoolYearData({
        id: "",
        title: "",
        startDate: "",
        endDate: "",
        isActive: false,
        type: "",
      });
      queryClient.invalidateQueries({ queryKey: ["schoolYears"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateClass = useMutation({
    mutationFn: () =>
      classeController.create({
        designation: newClass.name,
        code: newClass.code,
      }),

    onSuccess: () => {
      setShowAddClassModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewClass({
        name: "",
        code: "",
        level: "",
        schoolYear: "",
      });
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateClass = useMutation({
    mutationFn: () =>
      classeController.update(editClassData.id, {
        designation: editClassData.name,
        code: editClassData.code,
      }),

    onSuccess: (data) => {
      setShowEditClassModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditClassData({
        id: null,
        name: "",
        code: "",
        level: "",
        schoolYear: "",
      });
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateDomain = useMutation({
    mutationFn: () =>
      domainController.create({
        designation: newDomain.designation,
        code: newDomain.description,
      }),

    onSuccess: () => {
      setShowAddDomaineModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewDomain({
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["domains"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateDomain = useMutation({
    mutationFn: () =>
      domainController.update(editDomainData.id, {
        designation: editDomainData.designation,
        code: editDomainData.description,
      }),

    onSuccess: () => {
      setShowEditDomaineModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditDomainData({
        id: null,
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["domains"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateFunction = useMutation({
    mutationFn: () =>
      functionController.create({
        designation: newFunction.designation,
        code: newFunction.description,
      }),

    onSuccess: () => {
      setShowAddFunctionModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewFunction({
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["functions"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateFunction = useMutation({
    mutationFn: () =>
      functionController.update(editFunctionData.id, {
        designation: editFunctionData.designation,
        code: editFunctionData.description,
      }),

    onSuccess: () => {
      setShowEditFunctionModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditFunctionData({
        id: null,
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["functions"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateGrade = useMutation({
    mutationFn: () =>
      gradeController.create({
        designation: newGrade.designation,
        code: newGrade.description,
      }),

    onSuccess: () => {
      setShowAddGradeModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewGrade({
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["grades"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateGrade = useMutation({
    mutationFn: () =>
      gradeController.update(editGradeData.id, {
        designation: editGradeData.designation,
        code: editGradeData.description,
      }),

    onSuccess: () => {
      setShowEditGradeModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditGradeData({
        id: null,
        designation: "",
        description: "",
      });
      queryClient.invalidateQueries({ queryKey: ["grades"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateMontant = useMutation({
    mutationFn: () =>
      montantController.create({
        montant: newMontant.montant,
        classe_id: newMontant.classe_id,
        annee_scolaire_id: activeYearId,
      }),

    onSuccess: (data) => {
      setShowAddMontantModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewMontant({
        montant: "",
        classe_id: "",
        annee_scolaire_id: "",
      });
      queryClient.invalidateQueries({ queryKey: ["montants"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateMontant = useMutation({
    mutationFn: () =>
      montantController.update(editMontantData.id, {
        montant: editMontantData.montant,
        classe_id: editMontantData.classe_id,
        annee_scolaire_id: editMontantData.annee_scolaire_id,
      }),

    onSuccess: () => {
      setShowEditMontantModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditMontantData({
        id: "",
        montant: "",
        classe_id: "",
        annee_scolaire_id: "",
      });
      queryClient.invalidateQueries({ queryKey: ["montants"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationCreateTerminal = useMutation({
    mutationFn: () =>
      terminalController.create({
        uid: newTerminal.uid,
        designation: newTerminal.designation,
      }),

    onSuccess: (data) => {
      setShowAddTerminalModal(false);
      showNotification("Enregistrement effectué avec succès", "success");
      setNewTerminal({
        uid: "",
        designation: "",
      });
      queryClient.invalidateQueries({ queryKey: ["terminaux"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdateTerminal = useMutation({
    mutationFn: () =>
      terminalController.update(editTerminalData.id, {
        uid: editTerminalData.uid,
        designation: editTerminalData.designation,
      }),

    onSuccess: () => {
      setShowEditTerminalModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setEditTerminalData({
        id: "",
        uid: "",
        designation: "",
      });
      queryClient.invalidateQueries({ queryKey: ["terminaux"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });
  const mutationActivateTerminal = useMutation({
    mutationFn: () => terminalController.activate(state_actions.currentId),

    onSuccess: () => {
      setShowEditTerminalModal(false);
      showNotification("Activation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      queryClient.invalidateQueries({ queryKey: ["terminaux"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });
  const mutationDesactivateTerminal = useMutation({
    mutationFn: () => terminalController.desactivate(state_actions.currentId),

    onSuccess: () => {
      setShowEditTerminalModal(false);
      showNotification("Désactivation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      queryClient.invalidateQueries({ queryKey: ["terminaux"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDeleteShoolYear = useMutation({
    mutationFn: () => schoolYearController.delete(state_actions.currentId),

    onSuccess: (data) => {
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });

      queryClient.invalidateQueries({ queryKey: ["schoolYears"] });
    },

    onError: (err) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDeleteClass = useMutation({
    mutationFn: () => classeController.delete(state_actions.currentId),

    onSuccess: (data) => {
      // console.log(data);
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDeleteDomain = useMutation({
    mutationFn: () => domainController.delete(state_actions.currentId),

    onSuccess: (data) => {
      // console.log(data);
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["domains"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  // Ajoutées : la suppression de Fonctions et Grades était prévue côté UI
  // (bouton "Supprimer") mais n'avait pas de mutation associée. On la calque
  // sur mutationDeleteDomain, qui suit exactement le même schéma.
  const mutationDeleteFunction = useMutation({
    mutationFn: () => functionController.delete(state_actions.currentId),

    onSuccess: () => {
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      queryClient.invalidateQueries({ queryKey: ["functions"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDeleteGrade = useMutation({
    mutationFn: () => gradeController.delete(state_actions.currentId),

    onSuccess: () => {
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      queryClient.invalidateQueries({ queryKey: ["grades"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDeleteMontant = useMutation({
    mutationFn: () => montantController.delete(state_actions.currentId),

    onSuccess: (data) => {
      // console.log(data);
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["montants"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDeleteTerminal = useMutation({
    mutationFn: () => terminalController.delete(state_actions.currentId),

    onSuccess: (data) => {
      // console.log(data);
      showNotification("Suppression effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["terminaux"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationActivateShoolYear = useMutation({
    mutationFn: () => schoolYearController.activate(state_actions.currentId),

    onSuccess: () => {
      showNotification("Activation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      queryClient.invalidateQueries({ queryKey: ["schoolYears"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationActivateClass = useMutation({
    mutationFn: () => classeController.activate(state_actions.currentId),

    onSuccess: (data) => {
      // console.log(data);
      showNotification("Activation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationDesactivateClass = useMutation({
    mutationFn: () => classeController.desactivate(state_actions.currentId),

    onSuccess: (data) => {
      showNotification("Activation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const handleEditClass = () => {
    if (!editClassData.name.trim() || !editClassData.code.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationUpdateClass.mutate();
  };
  const handleSaveNewClass = () => {
    if (!newClass.name.trim() || !newClass.code.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationCreateClass.mutate();
  };
  const handleEditDomain = () => {
    if (
      !editDomainData.designation.trim() ||
      !editDomainData.description.trim()
    )
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationUpdateDomain.mutate();
  };
  const handleSaveNewDomain = () => {
    if (!newDomain.designation.trim() || !newDomain.description.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationCreateDomain.mutate();
  };
  const handleEditFunction = () => {
    if (
      !editFunctionData.designation.trim() ||
      !editFunctionData.description.trim()
    )
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationUpdateFunction.mutate();
  };
  const handleSaveNewFunction = () => {
    if (!newFunction.designation.trim() || !newFunction.description.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationCreateFunction.mutate();
  };
  const handleEditGrade = () => {
    if (!editGradeData.designation.trim() || !editGradeData.description.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationUpdateGrade.mutate();
  };
  const handleSaveNewGrade = () => {
    if (!newGrade.designation.trim() || !newGrade.description.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationCreateGrade.mutate();
  };
  const handleEditMontant = () => {
    if (!editMontantData.montant || !editMontantData.classe_id)
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationUpdateMontant.mutate();
  };
  const handleSaveNewMontant = () => {
    if (!newMontant.montant || !newMontant.classe_id)
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationCreateMontant.mutate();
  };

  const handleEditTerminal = () => {
    if (!editTerminalData.uid.trim() || !editTerminalData.designation.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationUpdateTerminal.mutate();
  };

  const handleSaveNewTerminal = () => {
    if (!newTerminal.uid.trim() || !newTerminal.designation.trim())
      return showNotification(
        "Veuillez remplir tous les champs obligatoires",
        "erreur",
      );
    mutationCreateTerminal.mutate();
  };

  const handleEditYear = async () => {
    try {
      await schoolYearSchema.validate({
        schoolYear: editSchoolYearData.title,
        startDate: editSchoolYearData.startDate,
        endDate: editSchoolYearData.endDate,
      });
      mutationUpdateShoolYear.mutate();
    } catch (err: any) {
      showNotification(err.message, "error");
    }
  };

  const handleSaveNewYear = async () => {
    try {
      await schoolYearSchema.validate({
        schoolYear: newSchoolYear.title,
        startDate: newSchoolYear.startDate,
        endDate: newSchoolYear.endDate,
      });
      mutationCreateShoolYear.mutate();
    } catch (err: any) {
      showNotification(err.message, "error");
    }
  };
  const handleDelete = () => {
    if (
      state_actions.actionType === "delete" &&
      state_actions.actionSection === "schoolYear"
    ) {
      mutationDeleteShoolYear.mutate();
    } else if (
      state_actions.actionType === "delete" &&
      state_actions.actionSection === "class"
    ) {
      mutationDeleteClass.mutate();
    } else if (
      state_actions.actionType === "delete" &&
      state_actions.actionSection === "domain"
    ) {
      mutationDeleteDomain.mutate();
    } else if (
      state_actions.actionType === "delete" &&
      state_actions.actionSection === "function"
    ) {
      mutationDeleteFunction.mutate();
    } else if (
      state_actions.actionType === "delete" &&
      state_actions.actionSection === "grade"
    ) {
      mutationDeleteGrade.mutate();
    } else if (
      state_actions.actionType === "delete" &&
      state_actions.actionSection === "montant"
    ) {
      mutationDeleteMontant.mutate();
    } else if (
      state_actions.actionType === "delete" &&
      state_actions.actionSection === "terminal"
    ) {
      mutationDeleteTerminal.mutate();
    }
  };

  const handleActivate = () => {
    if (
      state_actions.actionType === "activate" &&
      state_actions.actionSection === "schoolYear"
    ) {
      mutationActivateShoolYear.mutate();
    } else if (
      state_actions.actionType === "activate" &&
      state_actions.actionSection === "class"
    ) {
      mutationActivateClass.mutate();
    } else if (
      state_actions.actionType === "activate" &&
      state_actions.actionSection === "terminal"
    ) {
      mutationActivateTerminal.mutate();
    }
  };
  const handleDesactivate = () => {
    if (
      state_actions.actionType === "desactivate" &&
      state_actions.actionSection === "class"
    ) {
      mutationDesactivateClass.mutate();
    } else if (
      state_actions.actionType === "desactivate" &&
      state_actions.actionSection === "terminal"
    ) {
      mutationDesactivateTerminal.mutate();
    }
  };

  const handleResetFilter = () => {
    setFilterMontantBySy(activeYear || "");
    setFilterMontantByClasse("Toutes les classes");
  };

  // ===== Styles réutilisés pour la cohérence visuelle =====
  const cardClass = `overflow-hidden rounded-2xl border shadow-sm ${
    isDarkMode
      ? "border-[#30363D] bg-[#0D1117] shadow-black/10"
      : "border-gray-200 bg-white shadow-gray-200/40"
  }`;
  const panelBodyClass = `p-4 sm:p-5 ${
    isDarkMode ? "bg-[#0D1117]" : "bg-white"
  }`;
  const tableWrapClass = `overflow-auto rounded-xl border ${
    isDarkMode ? "border-[#30363D]" : "border-gray-200"
  }`;
  const theadClass = isDarkMode ? "bg-[#161B22]" : "bg-gray-50/80";
  const thClass = `px-4 py-3 text-left text-[9px] font-semibold uppercase tracking-[0.08em] ${
    isDarkMode ? "text-gray-500" : "text-gray-500"
  }`;
  const thClassCenter = `w-[132px] px-4 py-3 text-center text-[9px] font-semibold uppercase tracking-[0.08em] ${
    isDarkMode ? "text-gray-500" : "text-gray-500"
  }`;
  const tbodyClass = `${isDarkMode ? "bg-[#0D1117]" : "bg-white"} divide-y ${
    isDarkMode ? "divide-[#21262D]" : "divide-gray-100"
  }`;
  const addButtonClass = `inline-flex h-9 items-center gap-2 rounded-lg px-3.5 text-[11px] font-semibold transition-colors ${
    isDarkMode
      ? "bg-blue-600 text-white hover:bg-blue-500"
      : "bg-blue-600 text-white hover:bg-blue-700"
  } !rounded-button whitespace-nowrap`;
  const iconBtnClass = (color: "green" | "red" | "gray") => {
    const map: Record<string, string> = {
      green: isDarkMode
        ? "border-[#30363D] text-emerald-400 hover:border-emerald-500/30 hover:bg-emerald-500/10"
        : "border-gray-200 text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50",
      red: isDarkMode
        ? "border-[#30363D] text-red-400 hover:border-red-500/30 hover:bg-red-500/10"
        : "border-gray-200 text-red-600 hover:border-red-200 hover:bg-red-50",
      gray: isDarkMode
        ? "border-[#30363D] text-gray-600"
        : "border-gray-200 text-gray-400",
    };
    return `flex h-8 w-8 items-center justify-center rounded-lg border transition-all duration-150 !rounded-button whitespace-nowrap ${map[color]}`;
  };
  const modalOverlayClass =
    "fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm";
  const modalCardClass = `${
    isDarkMode
      ? "border-[#30363D] bg-[#161B22] text-gray-100"
      : "border-gray-200 bg-white text-gray-900"
  } w-full max-w-md overflow-hidden rounded-2xl border shadow-2xl`;
  const modalHeaderClass = `flex items-center justify-between border-b px-5 py-4 ${
    isDarkMode ? "border-[#30363D]" : "border-gray-200"
  }`;
  const modalFooterClass = `flex justify-end gap-2 border-t px-5 py-3.5 ${
    isDarkMode
      ? "border-[#30363D] bg-[#0D1117]/35"
      : "border-gray-200 bg-gray-50/60"
  }`;
  const inputClass = `h-10 w-full rounded-lg border px-3 text-xs outline-none transition-all ${
    isDarkMode
      ? "border-[#30363D] bg-[#0D1117] text-gray-200 placeholder:text-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
      : "border-gray-200 bg-white text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
  }`;
  const labelClass = `mb-1.5 block text-[10px] font-semibold ${
    isDarkMode ? "text-gray-400" : "text-gray-600"
  }`;
  const cancelBtnClass = `inline-flex h-9 items-center justify-center rounded-lg border px-4 text-[11px] font-semibold transition-colors ${
    isDarkMode
      ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
      : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
  } !rounded-button whitespace-nowrap`;
  const saveBtnClass = `inline-flex h-9 items-center justify-center rounded-lg bg-blue-600 px-4 text-[11px] font-semibold text-white transition-colors hover:bg-blue-700 !rounded-button whitespace-nowrap`;
  const filterButtonClass = `flex h-9 items-center gap-2 rounded-lg border px-3 text-[10px] font-medium transition-colors ${
    isDarkMode
      ? "border-[#30363D] bg-[#161B22] text-gray-300 hover:bg-[#21262D]"
      : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
  } !rounded-button whitespace-nowrap`;
  const dropdownPanelClass = `absolute right-0 top-full z-20 mt-1.5 max-h-64 w-52 overflow-y-auto rounded-xl border p-1 shadow-xl ${
    isDarkMode
      ? "border-[#30363D] bg-[#161B22] shadow-black/30"
      : "border-gray-200 bg-white shadow-gray-200/70"
  }`;
  const dropdownItemClass = `block w-full rounded-lg px-3 py-2 text-left text-[11px] transition-colors ${
    isDarkMode
      ? "text-gray-300 hover:bg-[#21262D]"
      : "text-gray-600 hover:bg-gray-100"
  } !rounded-button whitespace-nowrap`;
  const statusPill = (
    active: boolean,
    labelActive: string,
    labelInactive: string,
  ) => (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] font-semibold ${
        active
          ? isDarkMode
            ? "bg-emerald-500/10 text-emerald-400"
            : "bg-emerald-50 text-emerald-600"
          : isDarkMode
            ? "bg-red-500/10 text-red-400"
            : "bg-red-50 text-red-600"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          active ? "bg-emerald-500" : "bg-red-500"
        }`}
      ></span>
      {active ? labelActive : labelInactive}
    </span>
  );

  const TABS = [
    {
      id: "anneeScolaire",
      label: "Année scolaire et classe",
      icon: "fa-calendar-alt",
      restricted: false,
    },
    {
      id: "grades",
      label: "Grades et fonctions",
      icon: "fa-user-tie",
      restricted: true,
    },
    {
      id: "frais",
      label: "Frais scolaires",
      icon: "fa-sack-dollar",
      restricted: false,
    },
    {
      id: "terminaux",
      label: "Terminaux POS",
      icon: "fa-credit-card",
      restricted: true,
    },
  ];

  return (
    <>
      {" "}
      {isLoadingClasses || isLoadingMontant ? (
        <ScolariteAccordionSkeleton isDarkMode={isDarkMode} />
      ) : (
        <div className="space-y-5">
          {/* ===== Barre d'onglets ===== */}
          <div
            className={`flex gap-1 overflow-x-auto rounded-xl border p-1 ${
              isDarkMode
                ? "border-[#30363D] bg-[#161B22]/70"
                : "border-gray-200 bg-gray-50/80"
            }`}
          >
            {TABS.map((tab) => {
              const button = (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex h-9 items-center gap-2 rounded-lg px-3.5 text-[11px] font-semibold whitespace-nowrap transition-all ${
                    activeTab === tab.id
                      ? isDarkMode
                        ? "bg-[#21262D] text-blue-400 shadow-sm"
                        : "bg-white text-blue-600 shadow-sm ring-1 ring-gray-200"
                      : isDarkMode
                        ? "text-gray-500 hover:bg-[#21262D]/70 hover:text-gray-300"
                        : "text-gray-500 hover:bg-white hover:text-gray-700"
                  }`}
                >
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-md text-[10px] ${
                      activeTab === tab.id
                        ? isDarkMode
                          ? "bg-blue-500/10 text-blue-400"
                          : "bg-blue-50 text-blue-600"
                        : isDarkMode
                          ? "bg-[#0D1117] text-gray-500"
                          : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <i className={`fas ${tab.icon}`}></i>
                  </span>
                  {tab.label}
                </button>
              );
              return tab.restricted ? (
                <HasRole
                  key={tab.id}
                  allowedRoles={["SuperAdministrateur", "Administrateur"]}
                >
                  {button}
                </HasRole>
              ) : (
                button
              );
            })}
          </div>

          {/* ===== Onglet : Année scolaire et classe ===== */}
          {activeTab === "anneeScolaire" && (
            <div className={cardClass}>
              <div className={panelBodyClass}>
                <div className="mb-4 flex flex-wrap gap-3 justify-between items-center">
                  <div className="flex items-center flex-wrap gap-2">
                    <span className="text-sm font-semibold">
                      Années scolaires
                    </span>
                    {statusPill(
                      true,
                      `En cours : ${activeYear || "Aucune année active"}`,
                      "",
                    )}
                  </div>

                  <HasRole
                    allowedRoles={["SuperAdministrateur", "Administrateur"]}
                  >
                    <button
                      id="addSchoolYearBtn"
                      onClick={() => {
                        setShowAddYearModal(true);
                      }}
                      className={addButtonClass}
                    >
                      <i className="fas fa-plus"></i>
                      <span className="hidden lg:inline">
                        Ajouter une année
                      </span>
                    </button>
                  </HasRole>
                  {/* Add School Year Modal */}
                  {(showAddYearModal || showEditModal) && (
                    <div className={modalOverlayClass}>
                      <div className={modalCardClass}>
                        <div className={modalHeaderClass}>
                          <h3 className="text-lg font-semibold flex items-center gap-2">
                            <i className="fas fa-calendar-alt text-blue-500"></i>
                            {showEditModal
                              ? "Modifier une année scolaire"
                              : "Ajouter une année scolaire"}
                          </h3>
                        </div>
                        <div className="px-5 py-4">
                          <div className="mb-4">
                            <label className={labelClass}>
                              Intitulé de l'année
                            </label>
                            <input
                              name="schoolYear"
                              type="text"
                              value={
                                showEditModal
                                  ? editSchoolYearData.title
                                  : newSchoolYear.title
                              }
                              onChange={(e) => {
                                showEditModal
                                  ? setEditSchoolYearData({
                                      ...editSchoolYearData,
                                      title: e.target.value,
                                    })
                                  : setNewSchoolYear({
                                      ...newSchoolYear,
                                      title: e.target.value,
                                    });
                              }}
                              placeholder="2026-2027"
                              className={inputClass}
                            />
                          </div>
                          <div className="mb-4">
                            <label className={labelClass}>Date de début</label>
                            <input
                              name="startDate"
                              type="date"
                              value={
                                showEditModal
                                  ? editSchoolYearData.startDate
                                  : newSchoolYear.startDate
                              }
                              onChange={(e) =>
                                showEditModal
                                  ? setEditSchoolYearData({
                                      ...editSchoolYearData,
                                      startDate: e.target.value,
                                    })
                                  : setNewSchoolYear({
                                      ...newSchoolYear,
                                      startDate: e.target.value,
                                    })
                              }
                              className={inputClass}
                            />
                          </div>
                          <div className="mb-4">
                            <label className={labelClass}>Date de fin</label>
                            <input
                              name="endDate"
                              type="date"
                              value={
                                showEditModal
                                  ? editSchoolYearData.endDate
                                  : newSchoolYear.endDate
                              }
                              onChange={(e) =>
                                showEditModal
                                  ? setEditSchoolYearData({
                                      ...editSchoolYearData,
                                      endDate: e.target.value,
                                    })
                                  : setNewSchoolYear({
                                      ...newSchoolYear,
                                      endDate: e.target.value,
                                    })
                              }
                              className={inputClass}
                            />
                          </div>
                          {showEditModal && (
                            <div className="mb-2">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={
                                    showEditModal
                                      ? editSchoolYearData.isActive
                                      : newSchoolYear.isActive
                                  }
                                  onChange={(e) =>
                                    showEditModal
                                      ? setEditSchoolYearData({
                                          ...editSchoolYearData,
                                          isActive: e.target.checked,
                                        })
                                      : setNewSchoolYear({
                                          ...newSchoolYear,
                                          isActive: e.target.checked,
                                        })
                                  }
                                  className="form-checkbox h-4 w-4 text-blue-600 rounded"
                                />

                                <span
                                  className={`ml-2 text-sm ${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-700"
                                  }`}
                                >
                                  Année active
                                </span>
                              </label>
                            </div>
                          )}
                        </div>
                        <div className={modalFooterClass}>
                          <button
                            onClick={() => {
                              if (showEditModal) {
                                setShowEditModal(false);
                                setSelectedItem(null);
                                setEditSchoolYearData({
                                  title: "",
                                  startDate: "",
                                  endDate: "",
                                  isActive: false,
                                  type: "",
                                });
                              } else {
                                setShowAddYearModal(false);
                                setNewSchoolYear({
                                  title: "",
                                  startDate: "",
                                  endDate: "",
                                  isActive: false,
                                });
                              }
                            }}
                            className={cancelBtnClass}
                          >
                            Annuler
                          </button>
                          <button
                            disabled={
                              mutationCreateShoolYear.isPending ||
                              mutationUpdateShoolYear.isPending
                            }
                            onClick={() => {
                              if (showEditModal) {
                                handleEditYear();
                              } else {
                                handleSaveNewYear();
                              }
                            }}
                            className={`${saveBtnClass} ${
                              mutationCreateShoolYear.isPending ||
                              mutationUpdateShoolYear.isPending
                                ? "cursor-not-allowed opacity-70"
                                : ""
                            }`}
                          >
                            {mutationCreateShoolYear.isPending ||
                            mutationUpdateShoolYear.isPending
                              ? "Enregistrement..."
                              : "Enregistrer"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className={`mb-6 ${tableWrapClass}`}>
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead className={theadClass}>
                      <tr>
                        <th className={thClass}>Année</th>
                        <th className={thClass}>Date de début</th>
                        <th className={thClass}>Date de fin</th>
                        <th className={thClass}>Statut</th>
                        <HasRole
                          allowedRoles={[
                            "SuperAdministrateur",
                            "Administrateur",
                          ]}
                        >
                          <th className={thClassCenter}></th>
                        </HasRole>
                      </tr>
                    </thead>
                    <tbody className={tbodyClass}>
                      {schoolYearArr
                        .sort((a, b) =>
                          b.designation.localeCompare(a.designation),
                        )
                        .map((year: any) => {
                          return (
                            <tr
                              key={year.id}
                              className={
                                isDarkMode
                                  ? "group transition-colors hover:bg-[#161B22]"
                                  : "group transition-colors hover:bg-gray-50/80"
                              }
                            >
                              <td
                                className={`px-4 py-3 whitespace-nowrap text-[11px] ${
                                  isDarkMode ? "text-gray-300" : "text-gray-600"
                                } font-semibold`}
                              >
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {year.designation}
                                </span>
                              </td>
                              <td
                                className={`px-4 py-3 whitespace-nowrap text-[11px] ${
                                  isDarkMode ? "text-gray-300" : "text-gray-600"
                                }`}
                              >
                                {moment(year.date_debut).format("DD/MM/YYYY")}
                              </td>
                              <td
                                className={`px-4 py-3 whitespace-nowrap text-[11px] ${
                                  isDarkMode ? "text-gray-300" : "text-gray-600"
                                }`}
                              >
                                {moment(year.date_fin).format("DD/MM/YYYY")}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {statusPill(
                                  !!year.status,
                                  "En cours",
                                  "Inactive",
                                )}
                              </td>
                              <HasRole
                                allowedRoles={[
                                  "SuperAdministrateur",
                                  "Administrateur",
                                ]}
                              >
                                <td className="w-[132px] px-4 py-3 whitespace-nowrap text-center">
                                  <div className="flex justify-center gap-1 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto">
                                    <button
                                      onClick={() => {
                                        setShowEditModal(true);
                                        setEditSchoolYearData({
                                          id: year.id,
                                          title: year.designation,
                                          startDate: year.date_debut,
                                          endDate: year.date_fin,
                                          isActive: year.status,
                                          type: "",
                                        });
                                      }}
                                      className={iconBtnClass("green")}
                                      title="Modifier"
                                    >
                                      <i className="fas fa-edit text-sm"></i>
                                    </button>
                                    <button
                                      onClick={() => {
                                        dispatch({
                                          type: "DELETE",
                                          payload: {
                                            actionText:
                                              "Êtes-vous sûr de vouloir supprimer cette année scolaire? Cette action est irréversible",
                                            currentId: year.id,
                                            actionSection: "schoolYear",
                                          },
                                        });
                                      }}
                                      className={iconBtnClass("red")}
                                      title="Supprimer"
                                    >
                                      <i className="fas fa-trash-alt text-sm"></i>
                                    </button>
                                    <button
                                      disabled={year.status}
                                      onClick={() => {
                                        dispatch({
                                          type: "ACTIVATE",
                                          payload: {
                                            actionText:
                                              "Êtes-vous sûr de vouloir activer cette année scolaire?",
                                            currentId: year.id,
                                            actionSection: "schoolYear",
                                          },
                                        });
                                      }}
                                      className={
                                        year.status
                                          ? iconBtnClass("green")
                                          : iconBtnClass("gray")
                                      }
                                      title="Activer"
                                    >
                                      <i className="fa-solid fa-circle-check text-sm"></i>
                                    </button>
                                  </div>
                                </td>
                              </HasRole>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <span className="text-sm font-semibold leading-tight mr-2">
                      Gestion des Classes
                    </span>

                    <span
                      className={`hidden sm:inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        isDarkMode
                          ? "bg-[#21262D] text-gray-400"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {classeDataArr.length} classe
                      {classeDataArr.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <HasRole
                    allowedRoles={["SuperAdministrateur", "Administrateur"]}
                  >
                    <button
                      id="addClassBtn"
                      onClick={() => setShowAddClassModal(true)}
                      className={addButtonClass}
                    >
                      <i className="fas fa-plus"></i>
                      <span className="hidden md:inline">
                        Ajouter une classe
                      </span>
                    </button>
                  </HasRole>
                  {(showAddClassModal || showEditClassModal) && (
                    <div className={modalOverlayClass}>
                      <div className={modalCardClass}>
                        <div className={modalHeaderClass}>
                          <h3 className="text-lg font-semibold flex items-center gap-2">
                            <i className="fas fa-chalkboard text-blue-500"></i>
                            {showEditClassModal
                              ? "Modifier la classe"
                              : "Ajouter une nouvelle classe"}
                          </h3>
                        </div>
                        <div className="px-5 py-4">
                          <div className="mb-4">
                            <label className={labelClass}>
                              Nom de la classe
                            </label>
                            <input
                              type="text"
                              value={
                                showEditClassModal
                                  ? editClassData.name
                                  : newClass.name
                              }
                              onChange={(e) =>
                                showEditClassModal
                                  ? setEditClassData({
                                      ...editClassData,
                                      name: e.target.value,
                                    })
                                  : setNewClass({
                                      ...newClass,
                                      name: e.target.value,
                                    })
                              }
                              placeholder="6ème A"
                              className={inputClass}
                            />
                          </div>
                          <div className="mb-4">
                            <label className={labelClass}>
                              Code de la classe
                            </label>
                            <input
                              type="text"
                              value={
                                showEditClassModal
                                  ? editClassData.code
                                  : newClass.code
                              }
                              onChange={(e) =>
                                showEditClassModal
                                  ? setEditClassData({
                                      ...editClassData,
                                      code: e.target.value,
                                    })
                                  : setNewClass({
                                      ...newClass,
                                      code: e.target.value,
                                    })
                              }
                              placeholder="6 Eb A"
                              className={inputClass}
                            />
                          </div>
                          <div className="mb-4">
                            <label className={labelClass}>Niveau</label>
                            <select
                              value={
                                showEditClassModal
                                  ? editClassData.level
                                  : newClass.level
                              }
                              onChange={(e) =>
                                showEditClassModal
                                  ? setEditClassData({
                                      ...editClassData,
                                      level: e.target.value,
                                    })
                                  : setNewClass({
                                      ...newClass,
                                      level: e.target.value,
                                    })
                              }
                              className={inputClass}
                            >
                              <option value="Collège">Ecole Secondaire</option>
                            </select>
                          </div>
                          <div className="mb-2">
                            <label className={labelClass}>Année scolaire</label>
                            <select className={inputClass}>
                              <option value="">{activeYear}</option>
                            </select>
                          </div>
                        </div>
                        <div className={modalFooterClass}>
                          <button
                            disabled={
                              mutationCreateClass.isPending ||
                              mutationUpdateClass.isPending
                            }
                            onClick={() => {
                              showEditClassModal
                                ? setShowEditClassModal(false)
                                : setShowAddClassModal(false);
                              setNewClass({
                                name: "",
                                code: "",
                                level: "",
                                schoolYear: "",
                              });
                            }}
                            className={`${cancelBtnClass} ${
                              mutationCreateClass.isPending ||
                              mutationUpdateClass.isPending
                                ? "cursor-not-allowed"
                                : ""
                            }`}
                          >
                            Annuler
                          </button>
                          <button
                            disabled={
                              mutationCreateClass.isPending ||
                              mutationUpdateClass.isPending
                            }
                            onClick={() => {
                              showEditClassModal
                                ? handleEditClass()
                                : handleSaveNewClass();
                            }}
                            className={`${saveBtnClass} ${
                              mutationCreateClass.isPending ||
                              mutationUpdateClass.isPending
                                ? "cursor-not-allowed opacity-70"
                                : ""
                            }`}
                          >
                            {mutationCreateClass.isPending ||
                            mutationUpdateClass.isPending
                              ? "Enregistrement en cours..."
                              : "Enregistrer"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <div className="flex flex-wrap gap-3 mb-4">
                    <div className="flex-grow relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                        <i className="fas fa-search text-sm"></i>
                      </div>
                      <input
                        type="text"
                        placeholder="Rechercher une classe..."
                        className={`w-full pl-10 pr-4 py-2.5 rounded-lg text-xs ${
                          isDarkMode
                            ? "bg-[#161B22] border-gray-700"
                            : "bg-white border-gray-300"
                        } border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>

                    <div className="relative" ref={yearClassDropdownRef}>
                      <button
                        onClick={toggleYearClassDropdown}
                        className={filterButtonClass}
                      >
                        <i className="fas fa-calendar mr-2 text-xs opacity-70"></i>
                        <span>{filtrerClasseByYear}</span>
                        <i className="fas fa-chevron-down ml-2 text-xs"></i>
                      </button>

                      {isYearClassDropdownOpen && (
                        <div className={dropdownPanelClass}>
                          <div className="py-1">
                            <button
                              onClick={() => {
                                setfiltrerClasseByYear("Toutes les années");
                                setIsYearClassDropdownOpen(false);
                              }}
                              className={dropdownItemClass}
                            >
                              Toutes les années
                            </button>

                            {/* {years.map((year, index) => {
                              console.log(year);
                              return (
                                <button
                                  key={index}
                                  onClick={() => {
                                    setfiltrerClasseByYear(year);
                                    setIsYearClassDropdownOpen(false);
                                  }}
                                  className={dropdownItemClass}
                                >
                                  {year}
                                </button>
                              );
                            })} */}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={tableWrapClass}>
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead className={theadClass}>
                      <tr>
                        <th className={thClass}>Nom</th>
                        <th className={thClass}>Code - Classe</th>
                        <th className={thClass}>Niveau</th>
                        <HasRole
                          allowedRoles={[
                            "SuperAdministrateur",
                            "Administrateur",
                          ]}
                        >
                          <th className={thClassCenter}></th>
                        </HasRole>
                      </tr>
                    </thead>
                    <tbody className={tbodyClass}>
                      {filteredClasses
                        .sort((a, b) =>
                          a.designation.localeCompare(b.designation),
                        )
                        .map((item, index) => (
                          <tr
                            key={index}
                            className={
                              isDarkMode
                                ? "group transition-colors hover:bg-[#161B22]"
                                : "group transition-colors hover:bg-gray-50/80"
                            }
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-[11px] font-semibold">
                              <span
                                className={`${
                                  isDarkMode ? "text-gray-300" : "text-gray-600"
                                }`}
                              >
                                {capitalizeFirstLetter(item.designation)}
                              </span>
                            </td>
                            <td
                              className={`px-4 py-3 whitespace-nowrap text-[11px] text-left ${
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              {capitalizeFirstLetter(item.code)}
                            </td>
                            <td
                              className={`px-4 py-3 whitespace-nowrap text-[11px] ${
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              Ecole Sécondaire
                            </td>
                            <HasRole
                              allowedRoles={[
                                "SuperAdministrateur",
                                "Administrateur",
                              ]}
                            >
                              <td className="w-[132px] px-4 py-3 whitespace-nowrap text-center">
                                <div className="flex justify-center gap-1 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto">
                                  <button
                                    onClick={() => {
                                      setEditClassData({
                                        id: item.id,
                                        name: item.designation,
                                        code: item.code,
                                        level: "",
                                        schoolYear: "",
                                      });
                                      setShowEditClassModal(true);
                                    }}
                                    className={iconBtnClass("green")}
                                    title="Modifier"
                                  >
                                    <i className="fas fa-edit text-sm"></i>
                                  </button>
                                  <button
                                    onClick={() => {
                                      dispatch({
                                        type: "DELETE",
                                        payload: {
                                          actionText:
                                            "Êtes-vous sûr de vouloir supprimer cette classe? Cette action est irréversible",
                                          currentId: item.id,
                                          actionSection: "class",
                                        },
                                      });
                                    }}
                                    className={iconBtnClass("red")}
                                    title="Supprimer"
                                  >
                                    <i className="fas fa-trash-alt text-sm"></i>
                                  </button>
                                  <button
                                    onClick={() => {
                                      item.status
                                        ? dispatch({
                                            type: "DESACTIVATE",
                                            payload: {
                                              actionText:
                                                "Êtes-vous sûr de vouloir desactiver cette classe?",
                                              currentId: item.id,
                                              actionSection: "class",
                                            },
                                          })
                                        : dispatch({
                                            type: "ACTIVATE",
                                            payload: {
                                              actionText:
                                                "Êtes-vous sûr de vouloir activer cette classe?",
                                              currentId: item.id,
                                              actionSection: "class",
                                            },
                                          });
                                    }}
                                    className={
                                      item.status
                                        ? iconBtnClass("green")
                                        : iconBtnClass("gray")
                                    }
                                    title={item.status ? "Actif" : "Inactif"}
                                  >
                                    <i className="fa-solid fa-circle-check text-sm"></i>
                                  </button>
                                </div>
                              </td>
                            </HasRole>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ===== Onglet : Grades et fonctions ===== */}
          <HasRole allowedRoles={["SuperAdministrateur", "Administrateur"]}>
            {activeTab === "grades" && (
              <div className={cardClass}>
                <div className={panelBodyClass}>
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <span className="text-sm font-semibold leading-tight mr-2">
                        Gestion des domaines d'études
                      </span>

                      <span
                        className={`hidden sm:inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          isDarkMode
                            ? "bg-[#21262D] text-gray-400"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {domainDataArr.length} domaine
                        {domainDataArr.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowAddDomaineModal(true)}
                      className={addButtonClass}
                    >
                      <i className="fas fa-plus"></i> Ajouter un domaine
                    </button>

                    {(showAddDomaineModal || showEditDomaineModal) && (
                      <div id="addDomainModal" className={modalOverlayClass}>
                        <div className={modalCardClass}>
                          <div className={modalHeaderClass}>
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                              <i className="fas fa-layer-group text-blue-500"></i>
                              {showEditDomaineModal
                                ? "Modifier domaine"
                                : "Ajouter un nouveau domaine"}
                            </h3>
                            <button
                              onClick={() => {
                                showEditDomaineModal
                                  ? setShowEditDomaineModal(false)
                                  : setShowAddDomaineModal(false);

                                setCharCount(0);
                              }}
                              className={`h-8 w-8 flex items-center justify-center rounded-full ${
                                isDarkMode
                                  ? "hover:bg-gray-700"
                                  : "hover:bg-gray-100"
                              } text-gray-500 !rounded-button whitespace-nowrap`}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>

                          <div className="px-5 py-4">
                            <div className="mb-4">
                              <label className={labelClass}>
                                Nom du domaine *
                              </label>
                              <input
                                type="text"
                                value={
                                  showEditDomaineModal
                                    ? editDomainData.designation
                                    : newDomain.designation
                                }
                                onChange={(e) => {
                                  showEditDomaineModal
                                    ? setEditDomainData((prev) => ({
                                        ...prev,
                                        designation: e.target.value,
                                      }))
                                    : setNewDomain((prev) => ({
                                        ...prev,
                                        designation: e.target.value,
                                      }));
                                }}
                                placeholder="Ex: Sciences appliquées"
                                className={inputClass}
                                required
                              />
                            </div>

                            <div className="mb-2">
                              <label className={labelClass}>
                                Description *
                              </label>
                              <textarea
                                value={
                                  showEditDomaineModal
                                    ? editDomainData.description
                                    : newDomain.description
                                }
                                onChange={(
                                  e: React.ChangeEvent<HTMLTextAreaElement>,
                                ) => {
                                  const value = e.target.value.slice(0, 300);
                                  showEditDomaineModal
                                    ? setEditDomainData((prev) => ({
                                        ...prev,
                                        description: value,
                                      }))
                                    : setNewDomain((prev) => ({
                                        ...prev,
                                        description: value,
                                      }));

                                  setCharCount(value.length);
                                }}
                                rows={4}
                                placeholder="Décrivez les matières et objectifs pédagogiques de ce domaine..."
                                maxLength={300}
                                className={`${inputClass} resize-none`}
                                required
                              />
                              <div
                                className={`text-xs mt-1 ${
                                  isDarkMode ? "text-gray-500" : "text-gray-400"
                                }`}
                              >
                                {charCount}/300 caractères
                              </div>
                            </div>
                          </div>

                          <div className={modalFooterClass}>
                            <button
                              disabled={
                                mutationCreateDomain.isPending ||
                                mutationUpdateDomain.isPending
                              }
                              onClick={() => {
                                showEditDomaineModal
                                  ? setShowEditDomaineModal(false)
                                  : setShowAddDomaineModal(false);

                                setCharCount(0);
                              }}
                              className={`${cancelBtnClass} ${
                                mutationCreateDomain.isPending ||
                                mutationUpdateDomain.isPending
                                  ? "cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              Annuler
                            </button>

                            <button
                              disabled={
                                mutationCreateDomain.isPending ||
                                mutationUpdateDomain.isPending
                              }
                              onClick={() => {
                                showEditDomaineModal
                                  ? handleEditDomain()
                                  : handleSaveNewDomain();

                                setCharCount(0);
                              }}
                              className={`${saveBtnClass} ${
                                mutationCreateDomain.isPending ||
                                mutationUpdateDomain.isPending
                                  ? "cursor-not-allowed opacity-70"
                                  : ""
                              }`}
                            >
                              {mutationCreateDomain.isPending ||
                              mutationUpdateDomain.isPending
                                ? "Enregistrement en cours..."
                                : "Enregistrer"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={`mb-6 ${tableWrapClass}`}>
                    <table className="min-w-full divide-y divide-gray-700">
                      <thead className={theadClass}>
                        <tr>
                          <th className={thClass}>Nom</th>
                          <th className={thClass}>Description</th>
                          <th className={thClassCenter}></th>
                        </tr>
                      </thead>
                      <tbody className={tbodyClass}>
                        {domainDataArr
                          .sort((a, b) =>
                            a.designation.localeCompare(b.designation),
                          )
                          .map((item) => (
                            <tr
                              key={item.id}
                              className={
                                isDarkMode
                                  ? "group transition-colors hover:bg-[#161B22]"
                                  : "group transition-colors hover:bg-gray-50/80"
                              }
                            >
                              <td className="px-4 py-3 whitespace-nowrap text-[11px] font-semibold">
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {capitalizeFirstLetter(item.designation)}
                                </span>
                              </td>
                              <td
                                className={`px-6 py-4 text-[11px] ${
                                  isDarkMode ? "text-gray-300" : "text-gray-600"
                                }`}
                              >
                                {item.code}
                              </td>
                              <td className="w-[132px] px-4 py-3 whitespace-nowrap text-center">
                                <div className="flex justify-center gap-1 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto">
                                  <button
                                    onClick={() => {
                                      setEditDomainData({
                                        id: item.id,
                                        designation: item.designation,
                                        description: item.code,
                                      });
                                      setShowEditDomaineModal(true);
                                    }}
                                    className={iconBtnClass("green")}
                                    title="Modifier"
                                  >
                                    <i className="fas fa-edit text-sm"></i>
                                  </button>
                                  <button
                                    disabled
                                    // onClick={() => {
                                    //   dispatch({
                                    //     type: "DELETE",
                                    //     payload: {
                                    //       actionText:
                                    //         "Êtes-vous sûr de vouloir supprimer ce domaine? Cette action est irréversible",
                                    //       currentId: item.id,
                                    //       actionSection: "domain",
                                    //     },
                                    //   });
                                    // }}
                                    className={`${iconBtnClass("gray")} cursor-not-allowed`}
                                    title="Supprimer"
                                  >
                                    <i className="fas fa-trash-alt text-sm"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <span className="text-sm font-semibold leading-tight mr-2">
                        Gestion des fonctions du personnel
                      </span>

                      <span
                        className={`hidden sm:inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          isDarkMode
                            ? "bg-[#21262D] text-gray-400"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {functionDataArr.length} fonction
                        {functionDataArr.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowAddFunctionModal(true)}
                      className={addButtonClass}
                    >
                      <i className="fas fa-plus"></i>
                      <span className="hidden lg:inline">
                        Ajouter une fonction
                      </span>
                    </button>
                  </div>

                  {(showAddFunctionModal || showEditFunctionModal) && (
                    <div className={modalOverlayClass}>
                      <div className={modalCardClass}>
                        <div className={modalHeaderClass}>
                          <h3 className="text-lg font-semibold flex items-center gap-2">
                            <i className="fas fa-user-tie text-blue-500"></i>
                            {showEditFunctionModal
                              ? "Modifier Fonction"
                              : "Ajouter une nouvelle fonction"}
                          </h3>
                          <button
                            onClick={() => {
                              showEditFunctionModal
                                ? setShowEditFunctionModal(false)
                                : setShowAddFunctionModal(false);
                            }}
                            className={`h-8 w-8 flex items-center justify-center rounded-full ${
                              isDarkMode
                                ? "hover:bg-gray-700"
                                : "hover:bg-gray-100"
                            } text-gray-500 !rounded-button whitespace-nowrap`}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>

                        <div className="px-5 py-4">
                          <div className="mb-4">
                            <label className={labelClass}>
                              Nom de la fonction *
                            </label>
                            <input
                              type="text"
                              value={
                                showEditFunctionModal
                                  ? editFunctionData.designation
                                  : newFunction.designation
                              }
                              onChange={(e) => {
                                showEditFunctionModal
                                  ? setEditFunctionData((prev) => ({
                                      ...prev,
                                      designation: e.target.value,
                                    }))
                                  : setNewFunction((prev) => ({
                                      ...prev,
                                      designation: e.target.value,
                                    }));
                              }}
                              placeholder="Ex: Professeur principal"
                              className={inputClass}
                              required
                            />
                          </div>

                          <div className="mb-2">
                            <label className={labelClass}>
                              Description des responsabilités *
                            </label>
                            <textarea
                              id="functionDescription"
                              name="functionDescription"
                              rows={4}
                              value={
                                showEditFunctionModal
                                  ? editFunctionData.description
                                  : newFunction.description
                              }
                              onChange={(
                                e: React.ChangeEvent<HTMLTextAreaElement>,
                              ) => {
                                const value = e.target.value.slice(0, 300);
                                showEditFunctionModal
                                  ? setEditFunctionData((prev) => ({
                                      ...prev,
                                      description: value,
                                    }))
                                  : setNewFunction((prev) => ({
                                      ...prev,
                                      description: value,
                                    }));

                                setCharCountFunction(value.length);
                              }}
                              placeholder="Décrivez les principales responsabilités..."
                              maxLength={300}
                              className={`${inputClass} resize-none`}
                              required
                            ></textarea>
                            <div
                              className={`text-xs mt-1 ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              {charCountFunction}/300 caractères
                            </div>
                          </div>

                          <div className={modalFooterClass}>
                            <button
                              disabled={
                                mutationCreateFunction.isPending ||
                                mutationUpdateFunction.isPending
                              }
                              onClick={() => {
                                showEditFunctionModal
                                  ? setShowEditFunctionModal(false)
                                  : setShowAddFunctionModal(false);

                                setCharCountFunction(0);
                              }}
                              className={`${cancelBtnClass} ${
                                mutationCreateFunction.isPending ||
                                mutationUpdateFunction.isPending
                                  ? "cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              Annuler
                            </button>

                            <button
                              disabled={
                                mutationCreateFunction.isPending ||
                                mutationUpdateFunction.isPending
                              }
                              onClick={() => {
                                showEditFunctionModal
                                  ? handleEditFunction()
                                  : handleSaveNewFunction();

                                setCharCountFunction(0);
                              }}
                              className={`${saveBtnClass} ${
                                mutationCreateFunction.isPending ||
                                mutationUpdateFunction.isPending
                                  ? "cursor-not-allowed opacity-70"
                                  : ""
                              }`}
                            >
                              {mutationCreateFunction.isPending ||
                              mutationUpdateFunction.isPending
                                ? "Enregistrer en cours..."
                                : "Enregistrer"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={`mb-6 ${tableWrapClass}`}>
                    <table className="min-w-full divide-y divide-gray-700">
                      <thead className={theadClass}>
                        <tr>
                          <th className={thClass}>Titre</th>
                          <th className={thClass}>Description</th>
                          <th className={thClassCenter}></th>
                        </tr>
                      </thead>
                      <tbody className={tbodyClass}>
                        {functionDataArr
                          .sort((a, b) =>
                            a.designation.localeCompare(b.designation),
                          )
                          .map((item) => (
                            <tr
                              key={item.id}
                              className={
                                isDarkMode
                                  ? "group transition-colors hover:bg-[#161B22]"
                                  : "group transition-colors hover:bg-gray-50/80"
                              }
                            >
                              <td className="px-4 py-3 whitespace-nowrap text-[11px] font-semibold">
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {capitalizeFirstLetter(item.designation)}
                                </span>
                              </td>
                              <td
                                className={`px-6 py-4 text-sm ${
                                  isDarkMode ? "text-gray-300" : "text-gray-600"
                                }`}
                              >
                                {capitalizeFirstLetter(item.code)}
                              </td>
                              <td className="w-[132px] px-4 py-3 whitespace-nowrap text-center">
                                <div className="flex justify-center gap-1 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto">
                                  <button
                                    onClick={() => {
                                      setEditFunctionData({
                                        id: item.id,
                                        designation: item.designation,
                                        description: item.code,
                                      });
                                      setShowEditFunctionModal(true);
                                    }}
                                    className={iconBtnClass("green")}
                                    title="Modifier"
                                  >
                                    <i className="fas fa-edit text-sm"></i>
                                  </button>
                                  <button
                                    disabled
                                    // onClick={() => {
                                    //   dispatch({
                                    //     type: "DELETE",
                                    //     payload: {
                                    //       actionText:
                                    //         "Êtes-vous sûr de vouloir supprimer cette fonction? Cette action est irréversible",
                                    //       currentId: item.id,
                                    //       actionSection: "function",
                                    //     },
                                    //   });
                                    // }}
                                    className={`${iconBtnClass("gray")} cursor-not-allowed`}
                                    title="Supprimer"
                                  >
                                    <i className="fas fa-trash-alt text-sm"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <span className="text-sm font-semibold leading-tight mr-2">
                        Gestion des grades
                      </span>

                      <span
                        className={`hidden sm:inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          isDarkMode
                            ? "bg-[#21262D] text-gray-400"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {gradeDataArr.length} grade
                        {gradeDataArr.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowAddGradeModal(true)}
                      className={addButtonClass}
                    >
                      <i className="fas fa-plus"></i>
                      <span className="hidden lg:inline">Ajouter un grade</span>
                    </button>

                    {(showAddGradeModal || showEditGradeModal) && (
                      <div id="addDomainModal" className={modalOverlayClass}>
                        <div className={modalCardClass}>
                          <div className={modalHeaderClass}>
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                              <i className="fas fa-medal text-blue-500"></i>
                              {showEditGradeModal
                                ? "Modifier grade"
                                : "Ajouter un nouveau grade"}
                            </h3>
                            <button
                              onClick={() => {
                                showEditGradeModal
                                  ? setShowEditGradeModal(false)
                                  : setShowAddGradeModal(false);

                                setCharCountGrade(0);
                              }}
                              className={`h-8 w-8 flex items-center justify-center rounded-full ${
                                isDarkMode
                                  ? "hover:bg-gray-700"
                                  : "hover:bg-gray-100"
                              } text-gray-500 !rounded-button whitespace-nowrap`}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>

                          <div className="px-5 py-4">
                            <div className="mb-4">
                              <label className={labelClass}>
                                Nom du grade *
                              </label>
                              <input
                                type="text"
                                value={
                                  showEditGradeModal
                                    ? editGradeData.designation
                                    : newGrade.designation
                                }
                                onChange={(e) => {
                                  showEditGradeModal
                                    ? setEditGradeData((prev) => ({
                                        ...prev,
                                        designation: e.target.value,
                                      }))
                                    : setNewGrade((prev) => ({
                                        ...prev,
                                        designation: e.target.value,
                                      }));
                                }}
                                placeholder="Ex: Sciences appliquées"
                                className={inputClass}
                                required
                              />
                            </div>

                            <div className="mb-2">
                              <label className={labelClass}>
                                Description *
                              </label>
                              <textarea
                                value={
                                  showEditGradeModal
                                    ? editGradeData.description
                                    : newGrade.description
                                }
                                onChange={(
                                  e: React.ChangeEvent<HTMLTextAreaElement>,
                                ) => {
                                  const value = e.target.value.slice(0, 300);
                                  showEditGradeModal
                                    ? setEditGradeData((prev) => ({
                                        ...prev,
                                        description: value,
                                      }))
                                    : setNewGrade((prev) => ({
                                        ...prev,
                                        description: value,
                                      }));

                                  setCharCountGrade(value.length);
                                }}
                                rows={4}
                                placeholder="Décrivez les attributions de ce grade..."
                                maxLength={300}
                                className={`${inputClass} resize-none`}
                                required
                              />
                              <div
                                className={`text-xs mt-1 ${
                                  isDarkMode ? "text-gray-500" : "text-gray-400"
                                }`}
                              >
                                {charCountGrade}/300 caractères
                              </div>
                            </div>
                          </div>

                          <div className={modalFooterClass}>
                            <button
                              disabled={
                                mutationCreateGrade.isPending ||
                                mutationUpdateGrade.isPending
                              }
                              onClick={() => {
                                showEditGradeModal
                                  ? setShowEditGradeModal(false)
                                  : setShowAddGradeModal(false);

                                setCharCountGrade(0);
                              }}
                              className={`${cancelBtnClass} ${
                                mutationCreateGrade.isPending ||
                                mutationUpdateGrade.isPending
                                  ? "cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              Annuler
                            </button>

                            <button
                              disabled={
                                mutationCreateGrade.isPending ||
                                mutationUpdateGrade.isPending
                              }
                              onClick={() => {
                                showEditGradeModal
                                  ? handleEditGrade()
                                  : handleSaveNewGrade();

                                setCharCountGrade(0);
                              }}
                              className={`${saveBtnClass} ${
                                mutationCreateGrade.isPending ||
                                mutationUpdateGrade.isPending
                                  ? "cursor-not-allowed opacity-70"
                                  : ""
                              }`}
                            >
                              {mutationCreateGrade.isPending ||
                              mutationUpdateGrade.isPending
                                ? "Enregistrer en cours..."
                                : "Enregistrer"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={tableWrapClass}>
                    <table className="min-w-full divide-y divide-gray-700">
                      <thead className={theadClass}>
                        <tr>
                          <th className={thClass}>Intitulé</th>
                          <th className={thClass}>Description</th>
                          <th className={thClassCenter}></th>
                        </tr>
                      </thead>
                      <tbody className={tbodyClass}>
                        {gradeDataArr
                          .sort((a, b) =>
                            a.designation.localeCompare(b.designation),
                          )
                          .map((item) => (
                            <tr
                              key={item.id}
                              className={
                                isDarkMode
                                  ? "group transition-colors hover:bg-[#161B22]"
                                  : "group transition-colors hover:bg-gray-50/80"
                              }
                            >
                              <td className="px-4 py-3 whitespace-nowrap text-[11px] font-semibold">
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {capitalizeFirstLetter(item.designation)}
                                </span>
                              </td>
                              <td
                                className={`px-6 py-4 text-sm ${
                                  isDarkMode ? "text-gray-300" : "text-gray-600"
                                }`}
                              >
                                {item.code}
                              </td>
                              <td className="w-[132px] px-4 py-3 whitespace-nowrap text-center">
                                <div className="flex justify-center gap-1 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto">
                                  <button
                                    onClick={() => {
                                      setEditGradeData({
                                        id: item.id,
                                        designation: item.designation,
                                        description: item.code,
                                      });
                                      setShowEditGradeModal(true);
                                    }}
                                    className={iconBtnClass("green")}
                                    title="Modifier"
                                  >
                                    <i className="fas fa-edit text-sm"></i>
                                  </button>
                                  <button
                                    disabled
                                    // onClick={() => {
                                    //   dispatch({
                                    //     type: "DELETE",
                                    //     payload: {
                                    //       actionText:
                                    //         "Êtes-vous sûr de vouloir supprimer ce grade? Cette action est irréversible",
                                    //       currentId: item.id,
                                    //       actionSection: "grade",
                                    //     },
                                    //   });
                                    // }}
                                    className={`  ${iconBtnClass("gray")} cursor-not-allowed`}
                                    title="Supprimer"
                                  >
                                    <i className="fas fa-trash-alt text-sm"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </HasRole>

          {/* ===== Onglet : Frais scolaires ===== */}
          {activeTab === "frais" && (
            <div className={cardClass}>
              <div className={panelBodyClass}>
                <div className="mb-4 flex justify-between items-center">
                  <h4 className="text-xs font-semibold">
                    Gestion des frais scolaires
                  </h4>
                  <button
                    onClick={() => setShowAddMontantModal(true)}
                    className={addButtonClass}
                  >
                    <i className="fas fa-plus"></i>
                    <span className="hidden lg:inline">Ajouter des frais</span>
                  </button>

                  {(showAddMontantModal || showEditMontantModal) && (
                    <div id="addTerminalModal" className={modalOverlayClass}>
                      <div
                        className={`${
                          isDarkMode ? "bg-[#1c2128]" : "bg-white"
                        } rounded-xl shadow-xl w-full max-w-lg`}
                      >
                        <div className={modalHeaderClass}>
                          <h3 className="text-lg font-semibold flex items-center gap-2">
                            <i className="fas fa-sack-dollar text-blue-500"></i>
                            {showEditMontantModal
                              ? "Modifier Frais scolaire"
                              : "Ajouter un frais scolaire"}
                          </h3>
                          <button
                            id="closeTerminalModalBtn"
                            className={`h-8 w-8 flex items-center justify-center rounded-full ${
                              isDarkMode
                                ? "hover:bg-gray-700"
                                : "hover:bg-gray-100"
                            } text-gray-500 !rounded-button whitespace-nowrap`}
                            onClick={() => {
                              showEditMontantModal
                                ? setShowEditMontantModal(false)
                                : setShowAddMontantModal(false);
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                        <div className="px-5 py-4">
                          {!isActiveMontantYear && (
                            <div
                              className={`mb-4 flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-[10px] leading-relaxed ${
                                isDarkMode
                                  ? "border-amber-500/20 bg-amber-500/10 text-amber-300"
                                  : "border-amber-200 bg-amber-50 text-amber-700"
                              }`}
                            >
                              <i className="fas fa-lock mt-0.5 text-[10px]"></i>
                              <span>
                                Les frais peuvent être ajoutés ou modifiés
                                uniquement pour l’année scolaire active
                                <strong> {activeYear || ""}</strong>. L’année{" "}
                                <strong>{filterMontantSy}</strong> est
                                disponible uniquement en consultation.
                              </span>
                            </div>
                          )}
                          <div className="mb-4">
                            <label className={labelClass}>Classe *</label>
                            <select
                              value={
                                showEditMontantModal
                                  ? editMontantData.classe_id
                                  : newMontant.classe_id
                              }
                              onChange={(e) => {
                                showEditMontantModal
                                  ? setEditMontantData((prev) => ({
                                      ...prev,
                                      classe_id: e.target.value,
                                    }))
                                  : setNewMontant((prev) => ({
                                      ...prev,
                                      classe_id: e.target.value,
                                    }));
                              }}
                              className={inputClass}
                              disabled={!isActiveMontantYear}
                              required
                            >
                              <option value="">Sélectionner une classe</option>
                              {classeDataArr
                                .sort((a, b) =>
                                  a.designation
                                    .toLowerCase()
                                    .localeCompare(b.designation.toLowerCase()),
                                )
                                .map((item) => (
                                  <option value={item.id} key={item.id}>
                                    {capitalizeFirstLetter(item.designation)}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <div className="mb-4">
                            <label className={labelClass}>
                              Année scolaire *
                            </label>
                            <select
                              onChange={(e) => {
                                showEditMontantModal
                                  ? setEditMontantData((prev) => ({
                                      ...prev,
                                      annee_scolaire_id: e.target.value,
                                    }))
                                  : setNewMontant((prev) => ({
                                      ...prev,
                                      annee_scolaire_id: e.target.value,
                                    }));
                              }}
                              className={inputClass}
                              disabled={!isActiveMontantYear}
                              required
                            >
                              <option value={activeYearId}>{activeYear}</option>
                            </select>
                          </div>
                          <div className="mb-2">
                            <label className={labelClass}>Montant</label>
                            <input
                              type="number"
                              value={
                                showEditMontantModal
                                  ? editMontantData.montant
                                  : newMontant.montant
                              }
                              onChange={(e) => {
                                showEditMontantModal
                                  ? setEditMontantData((prev) => ({
                                      ...prev,
                                      montant: e.target.value,
                                    }))
                                  : setNewMontant((prev) => ({
                                      ...prev,
                                      montant: e.target.value,
                                    }));
                              }}
                              placeholder="50 $"
                              min="1"
                              max="10000"
                              className={inputClass}
                              disabled={!isActiveMontantYear}
                              required
                            />
                          </div>
                        </div>
                        <div className={modalFooterClass}>
                          <button
                            disabled={
                              mutationCreateMontant.isPending ||
                              mutationUpdateMontant.isPending ||
                              !isActiveMontantYear
                            }
                            onClick={() => {
                              showEditMontantModal
                                ? setShowEditMontantModal(false)
                                : setShowAddMontantModal(false);
                            }}
                            className={`${cancelBtnClass} ${
                              mutationCreateMontant.isPending ||
                              mutationUpdateMontant.isPending
                                ? "cursor-not-allowed"
                                : ""
                            }`}
                          >
                            Annuler
                          </button>

                          <button
                            disabled={
                              mutationCreateMontant.isPending ||
                              mutationUpdateMontant.isPending
                            }
                            onClick={() => {
                              showEditMontantModal
                                ? handleEditMontant()
                                : handleSaveNewMontant();
                            }}
                            className={`${saveBtnClass} ${
                              mutationCreateMontant.isPending ||
                              mutationUpdateMontant.isPending ||
                              !isActiveMontantYear
                                ? "cursor-not-allowed opacity-70"
                                : ""
                            }`}
                          >
                            {mutationCreateMontant.isPending ||
                            mutationUpdateMontant.isPending
                              ? "Enregistrement en cours..."
                              : "Enregistrer"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <div className="flex flex-wrap gap-3 mb-4">
                    <div className="flex-grow relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                        <i className="fas fa-search text-sm"></i>
                      </div>
                      <input
                        value={searchQueryFrais}
                        onChange={(e) => setSearchQueryFrais(e.target.value)}
                        type="text"
                        placeholder="Rechercher des frais..."
                        className={`w-full pl-10 pr-4 py-2.5 rounded-lg text-xs ${
                          isDarkMode
                            ? "bg-[#161B22] border-gray-700"
                            : "bg-white border-gray-300"
                        } border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                    <div className="relative" ref={montantSyDropdownRef}>
                      <button
                        onClick={toggleMontantSyDropdown}
                        className={filterButtonClass}
                      >
                        <i className="fas fa-calendar mr-2 text-xs opacity-70"></i>
                        <span>Année: {filterMontantSy}</span>
                        <i className="fas fa-chevron-down ml-2 text-xs"></i>
                      </button>
                      {isMontantSyDropdownOpen && (
                        <div className={dropdownPanelClass}>
                          <div className="py-1">
                            {schoolYearArr.map((year, index) => {
                              return (
                                <button
                                  key={index}
                                  onClick={() => {
                                    setFilterMontantBySy(year.designation);
                                    setIsMontantSyDropdownOpen(false);
                                  }}
                                  className={dropdownItemClass}
                                >
                                  {year.designation}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="relative" ref={montantClassDropdownRef}>
                      <button
                        onClick={toggleMontantClassDropdown}
                        className={filterButtonClass}
                      >
                        <i className="fas fa-chalkboard mr-2 text-xs opacity-70"></i>
                        <span>{filterMontantByClasse}</span>
                        <i className="fas fa-chevron-down ml-2 text-xs"></i>
                      </button>
                      {isMontantClassDropdownOpen && (
                        <div className={dropdownPanelClass}>
                          <div className="py-1">
                            <button
                              onClick={() => {
                                setFilterMontantByClasse("Toutes les classes");
                                setIsMontantClassDropdownOpen(false);
                              }}
                              className={dropdownItemClass}
                            >
                              Toutes les classes
                            </button>

                            {classeDataArr
                              .sort((a, b) =>
                                a.designation.localeCompare(b.designation),
                              )
                              .map((classe, index) => {
                                return (
                                  <button
                                    key={index}
                                    onClick={() => {
                                      setFilterMontantByClasse(
                                        classe.designation,
                                      );
                                      setIsMontantClassDropdownOpen(false);
                                    }}
                                    className={dropdownItemClass}
                                  >
                                    {classe.designation.toUpperCase()}
                                  </button>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      className={`px-4 py-2 rounded-lg ${
                        isDarkMode
                          ? "bg-[#161B22] hover:bg-gray-700 border-gray-700"
                          : "bg-white hover:bg-gray-50 border-gray-300"
                      } border !rounded-button whitespace-nowrap`}
                      title="Reinitialiser"
                      onClick={handleResetFilter}
                    >
                      <i className="fas fa-filter text-xs"></i>
                    </button>
                  </div>
                </div>
                <div className={tableWrapClass}>
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead className={theadClass}>
                      <tr>
                        <th className={thClass}>Classe</th>
                        <th className={thClass}>Montant</th>
                        <th className={thClass}>Période</th>
                        <th className={thClass}>Obligatoire</th>
                        <th className={thClassCenter}></th>
                      </tr>
                    </thead>
                    <tbody className={tbodyClass}>
                      {filteredMontants
                        .sort((a, b) =>
                          a.classeDesignation.localeCompare(
                            b.classeDesignation,
                          ),
                        )
                        .map((item) => (
                          <tr
                            key={item.classe_id}
                            className={
                              isDarkMode
                                ? "group transition-colors hover:bg-[#161B22]"
                                : "group transition-colors hover:bg-gray-50/80"
                            }
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-[11px] font-semibol">
                              <span
                                className={`${
                                  isDarkMode ? "text-gray-300" : "text-gray-600"
                                }`}
                              >
                                {capitalizeFirstLetter(item.classeDesignation)}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-[11px] font-semibold">
                              {item.hasMontant ? (
                                `${item.montant} $`
                              ) : (
                                <span className="text-[10px] font-medium text-amber-500">
                                  Non défini
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-4 py-3 whitespace-nowrap text-[11px] ${
                                isDarkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              {filterMontantSy}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {statusPill(true, "Oui", "Non")}
                            </td>
                            <td className="w-[132px] px-4 py-3 whitespace-nowrap text-center">
                              <div className="flex justify-center gap-1 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto">
                                {item.hasMontant ? (
                                  <>
                                    <button
                                      onClick={() => {
                                        setEditMontantData({
                                          id: item.id,
                                          classDesignation:
                                            item.classeDesignation,
                                          montant: item.montant,
                                          classe_id: item.classe_id,
                                          annee_scolaire_id:
                                            item.annee_scolaire_id,
                                        });
                                        setShowEditMontantModal(true);
                                      }}
                                      className={iconBtnClass("green")}
                                      title="Modifier"
                                    >
                                      <i className="fas fa-edit text-sm"></i>
                                    </button>
                                    <button
                                      onClick={() => {
                                        dispatch({
                                          type: "DELETE",
                                          payload: {
                                            actionText:
                                              "Êtes-vous sûr de vouloir supprimer ce montant? Cette action est irréversible",
                                            currentId: item.id,
                                            actionSection: "montant",
                                          },
                                        });
                                      }}
                                      disabled={!isActiveMontantYear}
                                      className={
                                        isActiveMontantYear
                                          ? iconBtnClass("red")
                                          : `${iconBtnClass("gray")} cursor-not-allowed`
                                      }
                                      title={
                                        isActiveMontantYear
                                          ? "Supprimer"
                                          : "Suppression indisponible pour une année non active"
                                      }
                                    >
                                      <i className="fas fa-trash-alt text-sm"></i>
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setNewMontant((prev) => ({
                                        ...prev,
                                        classe_id: String(item.classe_id),
                                        annee_scolaire_id: isActiveMontantYear
                                          ? String(activeYearId)
                                          : "",
                                      }));
                                      setShowAddMontantModal(true);
                                    }}
                                    className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[9px] font-semibold transition-colors ${
                                      isActiveMontantYear
                                        ? isDarkMode
                                          ? "border-blue-500/20 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                                          : "border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
                                        : isDarkMode
                                          ? "border-[#30363D] text-gray-500 hover:bg-[#21262D]"
                                          : "border-gray-200 text-gray-500 hover:bg-gray-100"
                                    }`}
                                    title={
                                      isActiveMontantYear
                                        ? "Définir le frais"
                                        : `Consultation uniquement — année active : ${activeYear || ""}`
                                    }
                                  >
                                    <i
                                      className={`fas ${
                                        isActiveMontantYear
                                          ? "fa-plus"
                                          : "fa-lock"
                                      } text-[9px]`}
                                    ></i>
                                    <span>
                                      {isActiveMontantYear
                                        ? "Ajouter"
                                        : "Indisponible"}
                                    </span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ===== Onglet : Terminaux POS ===== */}
          <HasRole allowedRoles={["SuperAdministrateur", "Administrateur"]}>
            {activeTab === "terminaux" && (
              <div className={cardClass}>
                <div className={panelBodyClass}>
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <span className="text-sm font-semibold leading-tight mr-2">
                        Gestion des terminaux
                      </span>

                      <span
                        className={`hidden sm:inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          isDarkMode
                            ? "bg-[#21262D] text-gray-400"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {terminalDataArr.length} termina
                        {terminalDataArr.length > 1 ? "ux" : "l"}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowAddTerminalModal(true)}
                      className={addButtonClass}
                    >
                      <i className="fas fa-plus"></i>
                      <span className="hidden lg:inline">
                        Ajouter un terminal
                      </span>
                    </button>

                    {(showAddTerminalModal || showEditTerminalModal) && (
                      <div id="addDomainModal" className={modalOverlayClass}>
                        <div className={modalCardClass}>
                          <div className={modalHeaderClass}>
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                              <i className="fas fa-credit-card text-blue-500"></i>
                              {showEditTerminalModal
                                ? "Modifier terminal"
                                : "Ajouter un nouveau terminal"}
                            </h3>
                            <button
                              onClick={() => {
                                showEditTerminalModal
                                  ? setShowEditTerminalModal(false)
                                  : setShowAddTerminalModal(false);
                              }}
                              className={`h-8 w-8 flex items-center justify-center rounded-full ${
                                isDarkMode
                                  ? "hover:bg-gray-700"
                                  : "hover:bg-gray-100"
                              } text-gray-500 !rounded-button whitespace-nowrap`}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>

                          <div className="px-5 py-4">
                            <div className="mb-4">
                              <label className={labelClass}>
                                Nom du terminal *
                              </label>
                              <input
                                type="text"
                                value={
                                  showEditTerminalModal
                                    ? editTerminalData.designation
                                    : newTerminal.designation
                                }
                                onChange={(e) => {
                                  showEditTerminalModal
                                    ? setEditTerminalData((prev) => ({
                                        ...prev,
                                        designation: e.target.value,
                                      }))
                                    : setNewTerminal((prev) => ({
                                        ...prev,
                                        designation: e.target.value,
                                      }));
                                }}
                                placeholder="Ex: Post paiement"
                                className={inputClass}
                                required
                              />
                            </div>

                            <div className="mb-2">
                              <label className={labelClass}>
                                Identifiant du terminal *
                              </label>
                              <input
                                type="text"
                                value={
                                  showEditTerminalModal
                                    ? editTerminalData.uid
                                    : newTerminal.uid
                                }
                                onChange={(e) => {
                                  showEditTerminalModal
                                    ? setEditTerminalData((prev) => ({
                                        ...prev,
                                        uid: e.target.value,
                                      }))
                                    : setNewTerminal((prev) => ({
                                        ...prev,
                                        uid: e.target.value,
                                      }));
                                }}
                                placeholder="Ex:06ba423607t28a90"
                                className={inputClass}
                                required
                              />
                            </div>
                          </div>

                          <div className={modalFooterClass}>
                            <button
                              disabled={
                                mutationCreateTerminal.isPending ||
                                mutationUpdateTerminal.isPending
                              }
                              onClick={() => {
                                showEditTerminalModal
                                  ? setShowEditTerminalModal(false)
                                  : setShowAddTerminalModal(false);
                              }}
                              className={`${cancelBtnClass} ${
                                mutationCreateTerminal.isPending ||
                                mutationUpdateTerminal.isPending
                                  ? "cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              Annuler
                            </button>

                            <button
                              disabled={
                                mutationCreateTerminal.isPending ||
                                mutationUpdateTerminal.isPending
                              }
                              onClick={() => {
                                showEditTerminalModal
                                  ? handleEditTerminal()
                                  : handleSaveNewTerminal();
                              }}
                              className={`${saveBtnClass} ${
                                mutationCreateTerminal.isPending ||
                                mutationUpdateTerminal.isPending
                                  ? "cursor-not-allowed opacity-70"
                                  : ""
                              }`}
                            >
                              {mutationCreateTerminal.isPending ||
                              mutationUpdateTerminal.isPending
                                ? "Enregistrer en cours..."
                                : "Enregistrer"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={tableWrapClass}>
                    <table className="min-w-full divide-y divide-gray-700">
                      <thead className={theadClass}>
                        <tr>
                          <th className={thClass}>ID</th>
                          <th className={thClass}>Nom terminal</th>
                          <th className={thClass}>Identifiant</th>
                          <th className={thClass}>Statut</th>
                          <th className={thClassCenter}></th>
                        </tr>
                      </thead>
                      <tbody className={tbodyClass}>
                        {terminalDataArr
                          .sort((a, b) =>
                            b.designation
                              .toLowerCase()
                              .localeCompare(a.designation.toLowerCase()),
                          )
                          .map((item, index) => (
                            <tr
                              key={item.id}
                              className={
                                isDarkMode
                                  ? "group transition-colors hover:bg-[#161B22]"
                                  : "group transition-colors hover:bg-gray-50/80"
                              }
                            >
                              <td className="px-4 py-3 whitespace-nowrap text-[11px] font-semibold">
                                <span
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-600"
                                  }`}
                                >
                                  POS-00{index + 1}
                                </span>
                              </td>

                              <td
                                className={`px-4 py-3 whitespace-nowrap text-[11px] ${
                                  isDarkMode ? "text-gray-300" : "text-gray-600"
                                }`}
                              >
                                {capitalizeFirstLetter(item.designation)}
                              </td>

                              <td
                                className={`px-4 py-3 whitespace-nowrap text-[11px] font-mono ${
                                  isDarkMode ? "text-gray-300" : "text-gray-600"
                                }`}
                              >
                                {item.uid}
                              </td>

                              <td className="px-4 py-3 whitespace-nowrap">
                                {statusPill(!!item.status, "Actif", "Inactif")}
                              </td>

                              <td className="w-[132px] px-4 py-3 whitespace-nowrap text-center">
                                <div className="flex justify-center gap-1 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto">
                                  <button
                                    onClick={() => {
                                      setEditTerminalData({
                                        id: item.id,
                                        uid: item.uid,
                                        designation: item.designation,
                                      });
                                      setShowEditTerminalModal(true);
                                    }}
                                    className={iconBtnClass("green")}
                                    title="Modifier"
                                  >
                                    <i className="fas fa-edit text-sm"></i>
                                  </button>
                                  <button
                                    onClick={() => {
                                      dispatch({
                                        type: "DELETE",
                                        payload: {
                                          actionText:
                                            "Êtes-vous sûr de vouloir supprimer ce terminal? Cette action est irréversible",
                                          currentId: item.id,
                                          actionSection: "terminal",
                                        },
                                      });
                                    }}
                                    className={iconBtnClass("red")}
                                    title="Supprimer"
                                  >
                                    <i className="fas fa-trash-alt text-sm"></i>
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (item.status) {
                                        dispatch({
                                          type: "DESACTIVATE",
                                          payload: {
                                            actionText:
                                              "Êtes-vous sûr de vouloir désactiver ce terminal?",
                                            currentId: item.id,
                                            actionSection: "terminal",
                                          },
                                        });
                                      } else {
                                        dispatch({
                                          type: "ACTIVATE",
                                          payload: {
                                            actionText:
                                              "Êtes-vous sûr de vouloir activer ce terminal?",
                                            currentId: item.id,
                                            actionSection: "terminal",
                                          },
                                        });
                                      }
                                    }}
                                    className={
                                      item.status
                                        ? iconBtnClass("green")
                                        : iconBtnClass("gray")
                                    }
                                    title={
                                      item.status ? "Desactiver" : "Activer"
                                    }
                                  >
                                    <i className="fa-solid fa-circle-check text-sm"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </HasRole>

          {/* delete dialog */}
          {state_actions.actionType === "delete" && (
            <div className={modalOverlayClass}>
              <div className={`${modalCardClass} p-6`}>
                <div className="text-lg font-semibold mb-3 flex items-center gap-2 text-red-500">
                  <i className="fas fa-trash-alt"></i>
                  Confirmer la suppression
                </div>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
                >
                  {state_actions.actionText}
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    disabled={
                      mutationDeleteShoolYear.isPending ||
                      mutationDeleteClass.isPending ||
                      mutationDeleteDomain.isPending ||
                      mutationDeleteFunction.isPending ||
                      mutationDeleteGrade.isPending ||
                      mutationDeleteTerminal.isPending ||
                      mutationDeleteMontant.isPending
                    }
                    onClick={() =>
                      dispatch({
                        type: "CANCEL",
                        payload: {
                          actionText: "",
                          currentId: null,
                          actionSection: "",
                        },
                      })
                    }
                    className={`${cancelBtnClass} ${
                      mutationDeleteShoolYear.isPending ||
                      mutationDeleteClass.isPending ||
                      mutationDeleteDomain.isPending ||
                      mutationDeleteFunction.isPending ||
                      mutationDeleteGrade.isPending ||
                      mutationDeleteTerminal.isPending ||
                      mutationDeleteMontant.isPending
                        ? "cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    disabled={
                      mutationDeleteShoolYear.isPending ||
                      mutationDeleteClass.isPending ||
                      mutationDeleteDomain.isPending ||
                      mutationDeleteFunction.isPending ||
                      mutationDeleteGrade.isPending ||
                      mutationDeleteTerminal.isPending ||
                      mutationDeleteMontant.isPending
                    }
                    onClick={handleDelete}
                    className={`px-4 py-2 rounded-md text-sm font-medium bg-red-500 hover:bg-red-600 text-white !rounded-button whitespace-nowrap ${
                      mutationDeleteShoolYear.isPending ||
                      mutationDeleteClass.isPending ||
                      mutationDeleteDomain.isPending ||
                      mutationDeleteFunction.isPending ||
                      mutationDeleteGrade.isPending ||
                      mutationDeleteTerminal.isPending ||
                      mutationDeleteMontant.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationDeleteShoolYear.isPending ||
                    mutationDeleteClass.isPending ||
                    mutationDeleteDomain.isPending ||
                    mutationDeleteFunction.isPending ||
                    mutationDeleteGrade.isPending ||
                    mutationDeleteTerminal.isPending ||
                    mutationDeleteMontant.isPending
                      ? "Suppression en cours..."
                      : "Confirmer la suppression"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Activate dialog */}
          {state_actions.actionType === "activate" && (
            <div className={modalOverlayClass}>
              <div className={`${modalCardClass} p-6`}>
                <div className="text-lg font-semibold mb-3 flex items-center gap-2 text-blue-500">
                  <i className="fa-solid fa-circle-check"></i>
                  Confirmer l'activation
                </div>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
                >
                  {state_actions.actionText}
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    disabled={
                      mutationActivateShoolYear.isPending ||
                      mutationActivateClass.isPending ||
                      mutationActivateTerminal.isPending
                    }
                    onClick={() =>
                      dispatch({
                        type: "CANCEL",
                        payload: {
                          actionText: "",
                          currentId: "",
                          actionSection: "",
                        },
                      })
                    }
                    className={`${cancelBtnClass} ${
                      mutationActivateShoolYear.isPending ||
                      mutationActivateClass.isPending ||
                      mutationActivateTerminal.isPending
                        ? "cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    disabled={
                      mutationActivateShoolYear.isPending ||
                      mutationActivateClass.isPending
                    }
                    onClick={handleActivate}
                    className={`${saveBtnClass} ${
                      mutationActivateShoolYear.isPending ||
                      mutationActivateClass.isPending ||
                      mutationActivateTerminal.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationActivateShoolYear.isPending ||
                    mutationActivateClass.isPending ||
                    mutationActivateTerminal.isPending
                      ? "Activation en cours..."
                      : "Confirmer l'activation"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Desactivate dialog */}
          {state_actions.actionType === "desactivate" && (
            <div className={modalOverlayClass}>
              <div className={`${modalCardClass} p-6`}>
                <div className="text-lg font-semibold mb-3 flex items-center gap-2 text-red-500">
                  <i className="fas fa-ban"></i>
                  Confirmer la désactivation
                </div>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
                >
                  {state_actions.actionText}
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    disabled={
                      mutationDesactivateClass.isPending ||
                      mutationDesactivateTerminal.isPending
                    }
                    onClick={() =>
                      dispatch({
                        type: "CANCEL",
                        payload: {
                          actionText: "",
                          currentId: "",
                          actionSection: "",
                        },
                      })
                    }
                    className={`${cancelBtnClass} ${
                      mutationDesactivateClass.isPending ||
                      mutationDesactivateTerminal.isPending
                        ? "cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    disabled={
                      mutationDesactivateClass.isPending ||
                      mutationDesactivateTerminal.isPending
                    }
                    onClick={handleDesactivate}
                    className={`px-4 py-2 rounded-md text-sm font-medium bg-red-500 hover:bg-red-600 text-white !rounded-button whitespace-nowrap ${
                      mutationDesactivateClass.isPending ||
                      mutationDesactivateTerminal.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationDesactivateClass.isPending ||
                    mutationDesactivateTerminal.isPending
                      ? "Désactivation en cours..."
                      : "Confirmer la désactivation"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== NOTIFICATION ===== */}
          {notification && (
            <div
              className={`fixed bottom-4 text-[14px] right-4 px-6 py-3 rounded-lg text-white shadow-lg transition-all duration-500 z-[60] flex items-center gap-2 ${
                notification.type === "success" ? "bg-green-500" : "bg-red-500"
              }`}
            >
              <i
                className={`fas ${
                  notification.type === "success"
                    ? "fa-check-circle"
                    : "fa-circle-exclamation"
                }`}
              ></i>
              {notification.message}
            </div>
          )}
        </div>
      )}{" "}
    </>
  );
};

export default Scolarite;
